import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from '@amplitude/analytics-browser';

import { ItemSelectorGroup } from '@unified-checkout/design-system';

import {
  SINGLE_PAYMENT_LINKS_EVENTS,
  SINGLE_PAYMENT_LINKS_EVENTS_PARAMS,
} from '@app/dashboard/constants/tracking_events/payment_links/single';
import {
  CUSTOMER_DETAILS_SECTION_ITEMS,
  CUSTOMER_DETAILS_TYPES,
  SINGLE_PAYMENTS_FIELDS,
  SINGLE_PL_SECTIONS_DIMENSIONS,
} from '@app/dashboard/constants/payment-links/single-payments';
import type {
  ICustomerDetailsProps,
  IOneCustomerRef,
} from '@app/dashboard/types/innerLayout/payment_links/single_quick_link';

import { SectionLoader } from '../../../Loader';
import OneCustomer from './components/OneCustomer';
import MultipleCustomer from './components/MultipleCustomer';
import AdditionalInfo from './components/AdditionalInfo';

import styles from './customer-details.module.scss';

const CustomerDetails = ({
  fields,
  setFields,
  tags,
  setTags,
  fullPageStatus,
  customerDetailsType,
  setCustomerDetailsType,
}: ICustomerDetailsProps) => {
  const { t } = useTranslation();
  const oneCustomerRef = useRef<IOneCustomerRef>(null);
  const customerDetailsTypeRef = useRef(customerDetailsType);

  const customerDetailsTypeOptions = useMemo(
    () =>
      CUSTOMER_DETAILS_SECTION_ITEMS(t).map((item) => ({
        ...item,
        checked: item.value === customerDetailsType,
        onClickHandler: () => handleCustomerDetailsTypeChange(item.value),
      })),
    [customerDetailsType, t]
  );

  const handleCustomerDetailsTypeChange = (value: string) => {
    if (value === customerDetailsTypeRef.current) return;

    customerDetailsTypeRef.current = value;

    amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.CUSTOMER_MODE_SELECTED, {
      [SINGLE_PAYMENT_LINKS_EVENTS_PARAMS.CUSTOMER_MODE]: value,
    });
    setCustomerDetailsType(value);
  };

  useEffect(() => {
    if (
      customerDetailsType === CUSTOMER_DETAILS_TYPES.ONE &&
      fields[SINGLE_PAYMENTS_FIELDS.PAYMENT_LIMIT]
    ) {
      setFields((prev: any) => {
        const updatedFields = { ...prev };
        delete updatedFields[SINGLE_PAYMENTS_FIELDS.PAYMENT_LIMIT];
        return updatedFields;
      });
    }
  }, [customerDetailsType, fields]);

  useEffect(() => {
    customerDetailsTypeRef.current = customerDetailsType;
  }, [customerDetailsType]);

  return fullPageStatus.loading ? (
    <SectionLoader {...SINGLE_PL_SECTIONS_DIMENSIONS.CUSTOMER_DETAILS} />
  ) : (
    <div className={styles['customer-details']}>
      <p className={styles['customer-details__title']}>
        {t(
          'innerLayout.paymentLinksCreationPages.singlePaymentLink.sections.customerDetails.title'
        )}
      </p>

      <ItemSelectorGroup type="radio" items={customerDetailsTypeOptions} />

      {customerDetailsType === CUSTOMER_DETAILS_TYPES.ONE ? (
        <OneCustomer
          ref={oneCustomerRef}
          fields={fields}
          setFields={setFields}
        />
      ) : (
        <MultipleCustomer fields={fields} tags={tags} setTags={setTags} />
      )}

      <AdditionalInfo
        shouldFillData={!!oneCustomerRef.current?.shouldFillData}
        fields={fields}
        setFields={setFields}
        customerDetailsType={customerDetailsType}
      />
    </div>
  );
};

export default CustomerDetails;
